import { PhoneNumberE164, TelecomPhoneLineId } from "@sp-crm/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { RetryableErrorBanner } from "components/ui/retryable-error-banner";
import { Spinner } from "components/ui/spinner";
import React, { useCallback } from "react";
import { PersonalNumberWarning, PhoneNumberChoice } from "./phone-number-choice";

interface CallForwardingOptionsProps {
    forwardingPhoneNumber: PhoneNumberE164 | null;
    personalPhoneNumber: PhoneNumberE164 | null;
    onForwardTo: (forwardingPhoneNumber: PhoneNumberE164) => Promise<void>;
    onUsePersonalNumber: () => Promise<void>;
}

export const CallForwardingOptions: React.FC<CallForwardingOptionsProps> = ({
    forwardingPhoneNumber,
    personalPhoneNumber,
    onForwardTo,
    onUsePersonalNumber,
}) => (
    <PhoneNumberChoice
        label="Forward incoming calls to"
        otherPhoneNumber={forwardingPhoneNumber}
        personalPhoneNumber={personalPhoneNumber}
        personalNumberWarning={
            personalPhoneNumber ? null : (
                <PersonalNumberWarning
                    problem="No personal number is set, so incoming calls can't connect."
                    fix="Add one in Personal settings"
                />
            )
        }
        otherNumberWarning={null}
        saveFailureMessage="Unable to save the forwarding number."
        onUseOtherNumber={onForwardTo}
        onUsePersonalNumber={onUsePersonalNumber}
    />
);

interface CallForwardingSettingProps {
    phoneLineId: TelecomPhoneLineId;
}

export const CallForwardingSetting: React.FC<CallForwardingSettingProps> = ({
    phoneLineId,
}) => {
    const trpcClient = trpc.useTRPC();
    const forwarding = useQuery({
        ...trpcClient.telecom.getCallForwarding.queryOptions({ phoneLineId }),
    });
    const setForwarding = useMutation(
        trpcClient.telecom.setCallForwardingNumber.mutationOptions(),
    );
    const clearForwarding = useMutation(
        trpcClient.telecom.clearCallForwardingNumber.mutationOptions(),
    );

    const forwardTo = useCallback(
        async (forwardingPhoneNumber: PhoneNumberE164) => {
            try {
                await setForwarding.mutateAsync({ phoneLineId, forwardingPhoneNumber });
            } finally {
                await forwarding.refetch();
            }
        },
        [phoneLineId, setForwarding, forwarding],
    );

    const usePersonalNumber = useCallback(async () => {
        try {
            await clearForwarding.mutateAsync({ phoneLineId });
        } finally {
            await forwarding.refetch();
        }
    }, [phoneLineId, clearForwarding, forwarding]);

    if (forwarding.isError) {
        return (
            <RetryableErrorBanner
                message="We couldn't load your call forwarding settings."
                retrying={forwarding.isFetching}
                onRetry={() => {
                    forwarding.refetch().catch((): void => undefined);
                }}
            />
        );
    }
    if (!forwarding.data) {
        return <Spinner />;
    }

    return (
        <CallForwardingOptions
            forwardingPhoneNumber={forwarding.data.forwardingPhoneNumber}
            personalPhoneNumber={forwarding.data.personalPhoneNumber}
            onForwardTo={forwardTo}
            onUsePersonalNumber={usePersonalNumber}
        />
    );
};
